import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { OvertimeRequest } from "@/types/supabase-types"
import { OvertimeStatus } from "@/types/enums"


// Define props for SummaryView
interface SummaryViewProps {
  employeeId?: string;
  startDate?: Date;
  endDate?: Date;
  searchQuery?: string;
}

// Define a reason structure
interface OvertimeReason {
  main_title: string;
  reason: string | null;
}

// Extended type with joined overtime_reasons
interface OvertimeRequestWithReason extends Partial<OvertimeRequest> {
  overtime_reasons?: OvertimeReason | OvertimeReason[];
}

export function SummaryView({ employeeId, startDate, endDate, searchQuery }: SummaryViewProps) {
  const [overtimeHistory, setOvertimeHistory] = useState<OvertimeRequestWithReason[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchOvertimeData() {
      try {
        setIsLoading(true);
        
        let query = supabase
          .from('overtime_requests')
          .select(`
            hours, 
            status, 
            date, 
            approver_name, 
            approver_comment,
            overtime_reasons!justification(
              main_title,
              reason
            )
          `);
          
        // Filter by employee if provided
        if (employeeId) {
          query = query.eq('employee_id', employeeId);
        }

        // Filter by date range if provided
        if (startDate) {
          query = query.gte('date', format(startDate, 'yyyy-MM-dd'));
        }
        if (endDate) {
          query = query.lte('date', format(endDate, 'yyyy-MM-dd'));
        }
        
        const { data, error } = await query;
        
        if (error) throw error;
        
        setOvertimeHistory(data || []);
      } catch {
        // Error handled silently
      } finally {
        setIsLoading(false);
      }
    }

    fetchOvertimeData();
  }, [employeeId, startDate, endDate]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    // Apply search query filter client-side
    const filteredHistory = overtimeHistory.filter(record => {
      if (!searchQuery) return true; // No filter applied
      const lowerCaseQuery = searchQuery.toLowerCase();
      
      // Handle joined overtime_reasons data
      let justificationText = '';
      if (record.overtime_reasons) {
        if (Array.isArray(record.overtime_reasons) && record.overtime_reasons.length > 0) {
          justificationText = record.overtime_reasons[0].reason || record.overtime_reasons[0].main_title || '';
        } else {
          // Single object case
          const reasons = record.overtime_reasons as OvertimeReason;
          justificationText = reasons.reason || reasons.main_title || '';
        }
      }
        
      return (
        justificationText.toLowerCase().includes(lowerCaseQuery) ||
        (record.status || '').toLowerCase().includes(lowerCaseQuery) ||
        (record.approver_name || '').toLowerCase().includes(lowerCaseQuery) ||
        (record.approver_comment || '').toLowerCase().includes(lowerCaseQuery)
      );
    });

    if (filteredHistory.length === 0) {
      return { totalHours: 0, approvedHours: 0, approvalRate: 0 };
    }
    
    const approvedRequests = filteredHistory.filter(req => req.status === OvertimeStatus.APPROVED);
    const totalHours = filteredHistory.reduce((sum, req) => sum + (req.hours || 0), 0);
    const approvedHours = approvedRequests.reduce((sum, req) => sum + (req.hours || 0), 0);
    const approvalRate = filteredHistory.length > 0 
      ? (approvedRequests.length / filteredHistory.length) * 100 
      : 0;
      
    return {
      totalHours: parseFloat(totalHours.toFixed(1)),
      approvedHours: parseFloat(approvedHours.toFixed(1)),
      approvalRate: parseFloat(approvalRate.toFixed(1))
    };
  }, [overtimeHistory, searchQuery]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Skeleton className="h-[120px] w-full" />
        <Skeleton className="h-[120px] w-full" />
        <Skeleton className="h-[120px] w-full" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="border rounded-lg p-4">
        <h3 className="font-medium mb-2">Total Submitted Hours</h3>
        <p className="text-sm text-muted-foreground">Selected period</p>
        <p className="text-3xl font-bold mt-2">{summaryStats.totalHours} hours</p>
      </div>
      
      <div className="border rounded-lg p-4">
        <h3 className="font-medium mb-2">Total Approved Hours</h3>
        <p className="text-sm text-muted-foreground">Selected period</p>
        <p className="text-3xl font-bold mt-2">{summaryStats.approvedHours} hours</p>
      </div>
      
      <div className="border rounded-lg p-4">
        <h3 className="font-medium mb-2">Approval Rate</h3>
        <p className="text-sm text-muted-foreground">Approved / Total</p>
        <p className="text-3xl font-bold mt-2">{summaryStats.approvalRate}%</p>
      </div>
    </div>
  )
}
